import React, { useEffect, useState } from "react";
import {
  Stack,
  Typography,
  FormControlLabel,
  TextField,
  Switch,
  Button,
  Box,
  styled,
  CircularProgress,
  Alert,
} from "@mui/material";
import EmailProcessService, { EmailConfig } from "@/services/EmailProcessService";

// ===== Gray OFF / Blue ON switch =====
const ColoredSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": { color: "rgb(0 121 194)" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: theme.palette.primary.main,
  },
  "& .MuiSwitch-switchBase": { color: "#9e9e9e" },
  "& .MuiSwitch-track": { backgroundColor: "#bdbdbd" },
}));

// Normalize any backend boolean-like value to true/false
const normalizeBool = (v: unknown): boolean => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return ["true", "1", "yes"].includes(v.toLowerCase());
  return false;
};

const EmailLimitSettings: React.FC = () => {
  // UI state
  const [enableLimit, setEnableLimit] = useState(false);
const [value, setValue] = useState("");
const [touched, setTouched] = useState(false);

const [config, setConfig] = useState<EmailConfig | null>(null);
const [loading, setLoading] = useState(true);
const [loadError, setLoadError] = useState<string | null>(null);
const [saving, setSaving] = useState(false);
const [saveMsg, setSaveMsg] = useState<string | null>(null);
const [processId, setProcessId] = useState<number | null>(null);

  const userName = localStorage.getItem("user_name");

  // Fetch email configuration on component mount
  useEffect(() => {
    let mounted = true;
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const response = await EmailProcessService.getEmailConfig();
        if (!mounted) return;
        const cfg = response?.data;
        if (cfg) {
  setConfig(cfg);
  setProcessId(cfg.id ?? null);

  setEnableLimit(normalizeBool(cfg.is_active));

  setValue(
    typeof cfg.daily_processing_limit === "number"
      ? String(cfg.daily_processing_limit)
      : ""
  );

  setTouched(false);
}
      } catch (error) {
        if (!mounted) return;
        setLoadError(
          (error as any)?.response?.data?.message ||
            (error as Error)?.message ||
            "Failed to load email configuration."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchConfig();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!saveMsg) return;
    const t = setTimeout(() => setSaveMsg(null), 3000);
    return () => clearTimeout(t);
  }, [saveMsg]);

  // ---- Validation (no error until user interacts)
  const getError = (): string => {
    if (!touched) return "";
    if (value === "") return "";
    if (!/^\d{1,3}$/.test(value)) return "Only digits allowed (max 3).";
    const n = Number(value);
    if (n < 1 || n > 999) return "Enter a number between 1 and 999.";
    return "";
  };
  const error = getError();

  // ---- Handlers
  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) =>
    setEnableLimit(e.target.checked);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (/^\d{0,3}$/.test(next)) setValue(next); // only digits up to 3 chars
    setTouched(true);
  };

  // Handle apply button click
  const handleApply = async () => {
  setTouched(true);

  const n = Number(value);

  if (!/^\d{1,3}$/.test(value) || n < 1 || n > 999) {
    return;
  }

  if (!config || processId === null) {
    setSaveMsg("Configuration not loaded yet. Please wait.");
    return;
  }

  setSaving(true);
  setSaveMsg(null);

  try {
    const payload = {
  daily_processing_limit: n,
  is_active: enableLimit,
  email_id: userName,
};

    const response = await EmailProcessService.updateEmailConfig(
      processId,
      payload
    );

    if (response?.message) {
      setSaveMsg(response.message);
    } else {
      setSaveMsg("Configuration updated successfully");
    }

    const updated = response?.data;

    if (updated) {
      setConfig(updated);

      setEnableLimit(
        normalizeBool(updated.is_active)
      );

      setValue(
        String(updated.daily_processing_limit ?? "")
      );
    }
  } catch (error) {
    const errorMessage =
      (error as any)?.response?.data?.message ||
      (error as Error)?.message ||
      "Failed to save settings.";

    setSaveMsg(errorMessage);

    console.error("Update error:", error);
  } finally {
    setSaving(false);
  }
};

  // ---- Render
  if (loading && !config) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={3}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading configuration…</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
      }}
    >
      <Box
        sx={{
          p: 3,
          maxWidth: 420,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#ffff",
          borderRadius: 3,
        }}
      >
        <Stack spacing={2} sx={{ alignItems: "center", justifyContent: "center" }}>
          <Typography variant="h6">Email Process Settings</Typography>

          {/* {loadError && <Alert severity="error">{loadError}</Alert>}
          {saveMsg && (
            <Alert severity="success" sx={{ backgroundColor: "green", color: "#fff" }}>
              {saveMsg}
            </Alert>
          )} */}

          <FormControlLabel
            control={<ColoredSwitch checked={enableLimit} onChange={handleToggle} />}
            label="Enable Email Process Limit"
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
            <TextField
              label="Number of Email Process Limit"
              InputLabelProps={{
                style: { fontSize: "14px", color: "#000", marginBottom: "4px" },
              }}
              value={value}
              onChange={handleChange}
              onBlur={() => setTouched(true)}
              error={!!error}
              helperText={error || " "}
              placeholder="1–999"
              fullWidth
              // IMPORTANT: Use text + inputMode instead of number to allow regex/max length
              type="text"
              inputMode="numeric"
              inputProps={{
                pattern: "\\d*",
                maxLength: 3,
              }}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f2f2f2",
                  borderRadius: "6px",
                  "& fieldset": { border: "1px solid #e0e0e0" },
                  "&:hover fieldset": { border: "1px solid #d0d0d0" },
                  "&.Mui-focused fieldset": { border: "1px solid #d0d0d0" },
                },
                "& .MuiOutlinedInput-input": { padding: "10px 12px" },
              }}
            />
          </Box>

          <Button
  variant="contained"
  color="primary"
  onClick={handleApply}
  disabled={saving}
  sx={{
    backgroundColor: "#1b184d",
    width: "120px",
  }}
>
  {saving ? "Saving..." : "Submit"}
</Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default EmailLimitSettings;
